import { RefObject, MouseEvent } from 'react';
import {
  DefaultLinkClickData,
  OnSiteSearchClickLink,
  OnSiteSearchEvent,
  TrackingEvent,
} from '../../tracking-services/types';
import { TrackingService } from '@rocheglobal/component-library-dot-com/src/services/tracking';
import { AdobeTracking } from '../../tracking-services';
import { DomService } from '@rocheglobal/component-library-dot-com/src/services/dom';
import { DownloadService } from '../../services/download';

export const handleRocheTeaserClick = (
  event: MouseEvent,
  teaserTitle: string,
  teaserUrl: string,
  teaserRef: RefObject<HTMLDivElement>,
  isDownloadTeaser?: boolean,
) => {
  if (isDownloadTeaser) {
    handleDownloadTeaserClick(event, teaserUrl);
    return;
  }

  if (event.isTrusted) {
    const defaultTracking = {
      event: 'teaser_click',
      component_name: 'roche-teaser',
    };

    const adobeTracking: DefaultLinkClickData = {
      innerText: teaserTitle,
      name: teaserTitle,
      url: teaserUrl,
      position: 'rocheTeaser',
      type: 'Teaser',
      socialMedia: '',
      socialInteraction: '',
    };

    TrackingService.push({
      ...defaultTracking,
      additionalParameters: {
        teaser_title: teaserTitle,
        teaser_url: teaserUrl,
      },
    });

    const getLatestOnsiteSearchEvent = (): TrackingEvent =>
      window?.oneDataLayer?.filter((event: { event: string }) => event.event === 'onsiteSearch').pop();

    const getElementsInView = () =>
      Array.from(teaserRef.current?.parentElement?.querySelectorAll<HTMLElement>('div.teaser') || []);

    const toggleActiveState = (isActive: boolean): void => {
      if (isActive) {
        teaserRef.current.classList.add('active');
      } else {
        teaserRef.current.classList.remove('active');
      }
    };

    const getActiveElementIndex = (elementsInView: HTMLElement[]): number =>
      elementsInView.findIndex((element) => (element as HTMLElement).classList.contains('active'));

    const getResultsPerPage = (elementsInView: HTMLElement[]): number => {
      const container =
        teaserRef.current.closest('.roche-search') ||
        teaserRef.current.closest('roche-search-list-container') ||
        teaserRef.current.closest('roche-search');

      return container
        ? parseInt((container as Element)?.getAttribute('data-rpp') ?? elementsInView.length.toString(), 10)
        : elementsInView.length || 0;
    };

    const calculatePaginationData = (
      elementsInView: HTMLElement[],
    ): {
      paginationResultsPerPage: number;
      activeElementIndex: number;
    } => {
      toggleActiveState(true);

      const activeElementIndex = getActiveElementIndex(elementsInView as unknown as HTMLElement[]);
      const paginationResultsPerPage = getResultsPerPage(elementsInView as unknown as HTMLElement[]);

      toggleActiveState(false);

      return { paginationResultsPerPage, activeElementIndex };
    };

    const handleOnsiteSearchClick = (): void => {
      const latestSearchEvent = getLatestOnsiteSearchEvent();
      const elementsInView = getElementsInView();

      const { paginationResultsPerPage, activeElementIndex } = calculatePaginationData(elementsInView);

      const { term, results, filter, searchComponent } =
        (latestSearchEvent as OnSiteSearchEvent)?.content?.onsiteSearch || {};

      const url = new URL(teaserUrl);

      const resultsPosition = (filter?.pagination - 1) * paginationResultsPerPage + activeElementIndex + 1;

      AdobeTracking(
        {
          ...adobeTracking,
          domain: url.host,
          path: url.pathname,
          resultsPosition,
          term,
          results,
          filter,
          searchComponent,
        } as OnSiteSearchClickLink,
        'onsiteSearchClick',
      );
    };

    if (
      teaserRef.current.closest('.roche-search') ||
      teaserRef.current.closest('roche-search') ||
      teaserRef.current.closest('roche-search-list-container')
    ) {
      handleOnsiteSearchClick();
    } else {
      AdobeTracking(adobeTracking, 'defaultLinkClick');
    }
  }
};

const handleDownloadTeaserClick = (event: MouseEvent, teaserUrl: string) => {
  event.preventDefault();
  event.stopPropagation();

  if (DomService.getGlobalOnClickNotice()) {
    const event = new CustomEvent('triggerDownload', {
      detail: { url: teaserUrl },
      bubbles: true,
    });
    window.dispatchEvent(event);
    return;
  }

  DownloadService.handleDownload(teaserUrl);
};
